import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
  Activity,
  Users,
  Lightbulb,
  Calendar,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useDashboardStats } from '../hooks/useDashboardStats';

interface StatCard {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  subtitle?: string;
}

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

/**
 * Admin dashboard page with welcome message and overview stats
 */
export default function DashboardPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const { data: dashboardStats, isLoading } = useDashboardStats();

  const stats: StatCard[] = [
    {
      title: 'Total Activities',
      value: dashboardStats?.activities.total ?? '-',
      icon: <Activity className="w-6 h-6" />,
      subtitle: dashboardStats
        ? `${dashboardStats.activities.published} published, ${dashboardStats.activities.archived} archived`
        : undefined,
    },
    {
      title: 'Strategies',
      value: dashboardStats?.strategies.total ?? '-',
      icon: <Lightbulb className="w-6 h-6" />,
      subtitle: dashboardStats
        ? `${dashboardStats.strategies.published} published, ${dashboardStats.strategies.archived} archived`
        : undefined,
    },
    {
      title: 'Guest Users',
      value: dashboardStats?.guestUsers ?? '-',
      icon: <Users className="w-6 h-6" />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-pine-display font-display text-pine-ink mb-2">
          {getGreeting()}, {user?.name || 'Admin'}!
        </h1>
        <p className="text-body text-pine-ink-soft">
          Welcome to the Play Moments admin dashboard. Here&apos;s an overview of your platform.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
              </CardHeader>
              <CardContent>
                <p className="text-caption text-pine-ink-soft mb-1">
                  {stat.title}
                </p>
                {isLoading ? (
                  <div className="h-8 w-16 bg-pine-line animate-pulse rounded" />
                ) : (
                  <p className="text-heading-3 text-pine-ink font-semibold">
                    {stat.value}
                  </p>
                )}
                {stat.subtitle && !isLoading && (
                  <p className="text-caption text-pine-ink-soft mt-1">
                    {stat.subtitle}
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Card className="bg-pine-card border-pine-line shadow-pine-card rounded-pine-card">
          <CardHeader>
            <CardTitle className="text-heading-4 text-pine-ink">
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="default"
                className="h-auto flex flex-col items-start p-4 bg-pine-gold hover:bg-pine-gold-deep text-[#2A1F08] shadow-[0_2px_8px_rgba(232,161,60,.4)]"
                onClick={() => navigate('/admin/spark-activities')}
              >
                <Activity className="w-5 h-5 mb-2" />
                <p className="font-semibold">Activities</p>
                <p className="text-caption opacity-75">View Activites</p>
              </Button>
              <Button
                variant="secondary"
                className="h-auto flex flex-col items-start p-4 bg-white border-[1.5px] border-pine-pine text-pine-pine hover:bg-pine-hover-button"
                onClick={() => navigate('/admin/strategies/new')}
              >
                <Lightbulb className="w-5 h-5 mb-2" />
                <p className="font-semibold">Create Strategy</p>
                <p className="text-caption opacity-75">Add a new strategy</p>
              </Button>
              <Button
                variant="secondary"
                className="h-auto flex flex-col items-start p-4 bg-white border-[1.5px] border-pine-pine text-pine-pine hover:bg-pine-hover-button"
                onClick={() => navigate('/admin/calendar')}
              >
                <Calendar className="w-5 h-5 mb-2" />
                <p className="font-semibold">View Calendar</p>
                <p className="text-caption opacity-75">Activity schedule</p>
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
